import OpenAI from 'openai';
import { OPENAI_API_KEY, STORAGE_KEYS } from '../utils/constants';
import { ChatCompletionRequest, ChatCompletionMessage } from '../types/openai';

/**
 * Service for interacting with OpenAI APIs via Green Tokens
 */
class OpenAIService {
  private openai: OpenAI;
  private apiKey: string;

  constructor(apiKey: string = OPENAI_API_KEY) {
    this.apiKey = apiKey;
    this.openai = new OpenAI({
      apiKey: this.apiKey,
      baseURL: 'https://api.green-tokens.com/v1',
      dangerouslyAllowBrowser: true, // Allow browser usage
      timeout: 120000, // 2 minutes timeout for long requests like recipe scaling
    });
  }

  /**
   * Set the API key and reinitialize client
   * @param apiKey OpenAI API key
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
    this.openai = new OpenAI({
      apiKey: this.apiKey,
      baseURL: 'https://api.green-tokens.com/v1',
      dangerouslyAllowBrowser: true,
      timeout: 120000, // 2 minutes timeout for long requests like recipe scaling
    });
  }

  /**
   * Get the user's selected text model from settings
   * @returns The selected model or default
   */
  private getSelectedModel(): string {
    return localStorage.getItem(STORAGE_KEYS.TEXT_MODEL) || 'gpt-5-mini';
  }

  /**
   * Get chat completion from OpenAI
   * @param prompt User prompt
   * @param model OpenAI model to use (optional, defaults to user's setting)
   * @param maxTokens Maximum tokens to generate
   * @returns Promise resolving to generated text
   */
  async getChatCompletion(
    prompt: string,
    model?: string,
    maxTokens?: number
  ): Promise<string> {
    try {
      // Use provided model or fall back to user's selected model
      const selectedModel = model || this.getSelectedModel();

      console.log('🤖 LLM call');

      const requestOptions: ChatCompletionRequest = {
        model: selectedModel,
        messages: [{ role: 'user', content: prompt }],
      };

      if (maxTokens) {
        requestOptions.max_tokens = maxTokens;
      }

      const response = await this.openai.chat.completions.create(requestOptions);

      if (!response.choices || response.choices.length === 0) {
        throw new Error('No choices received from chat completion');
      }

      const content = response.choices[0]?.message?.content;
      console.log('✅ LLM response');
      return content || '';
    } catch (error) {
      console.error('Error getting chat completion:', error);
      throw error;
    }
  }

  /**
   * Multi-turn chat completion with message history
   * @param messages Array of chat messages (user/assistant/system)
   * @param options Optional: model, maxTokens, temperature
   * @returns Promise resolving to generated assistant text
   */
  async chatWithMessages(
    messages: ChatCompletionMessage[],
    options?: { model?: string; maxTokens?: number; temperature?: number }
  ): Promise<string> {
    try {
      const selectedModel = options?.model || this.getSelectedModel();
      const requestOptions: ChatCompletionRequest = {
        model: selectedModel,
        messages,
      };
      if (options?.maxTokens) {
        requestOptions.max_tokens = options.maxTokens;
      }
      if (options?.temperature) {
        requestOptions.temperature = options.temperature;
      }
      const response = await this.openai.chat.completions.create(requestOptions);
      if (!response.choices || response.choices.length === 0) {
        throw new Error('No choices received from chat completion');
      }
      const content = response.choices[0]?.message?.content;
      return content || '';
    } catch (error) {
      console.error('Error in chatWithMessages:', error);
      throw error;
    }
  }

  /**
   * Chat completion with tool calling support
   * @param messages Array of chat messages
   * @param tools Array of tool definitions
   * @param options Optional: model, temperature
   * @returns Promise resolving to the full assistant message (including tool_calls)
   */
  async chatWithTools(
    messages: ChatCompletionMessage[],
    tools: OpenAI.Chat.ChatCompletionTool[],
    options?: { model?: string; temperature?: number }
  ): Promise<OpenAI.Chat.ChatCompletionMessage> {
    try {
      const selectedModel = options?.model || this.getSelectedModel();

      console.log('🤖 LLM call');

      const requestOptions: any = {
        model: selectedModel,
        messages,
        tools,
      };
      if (options?.temperature) {
        requestOptions.temperature = options.temperature;
      }
      const response = await this.openai.chat.completions.create(requestOptions);
      if (!response.choices || response.choices.length === 0) {
        throw new Error('No choices received from chat completion');
      }
      const message = response.choices[0]?.message;
      if (!message) {
        throw new Error('No message received from chat completion');
      }

      console.log('✅ LLM response');
      return message;
    } catch (error) {
      console.error('Error in chatWithTools:', error);
      throw error;
    }
  }

  /**
   * Get GPT Vision analysis of an image
   * @param imageFile Image file to analyze
   * @param prompt Prompt for GPT Vision
   * @param maxTokens Maximum tokens to generate (undefined for no limit)
   * @returns Promise resolving to generated text
   */
  async getGPTVision(
    imageFile: File,
    prompt: string,
    maxTokens?: number
  ): Promise<string> {
    try {
      console.log('🖼️ Vision analysis');

      // Convert image to base64
      const base64Image = await this.fileToBase64(imageFile);

      const requestOptions: ChatCompletionRequest = {
        model: 'gpt-5-mini', // Vision models need to be gpt-5-mini or gpt-4-vision-preview
        messages: [
          {
            role: 'user',
            content: [
              { type: 'text', text: prompt },
              {
                type: 'image_url',
                image_url: {
                  url: `data:image/jpeg;base64,${base64Image}`,
                },
              },
            ] as any, // OpenAI types don't fully support vision content yet
          },
        ],
      };
      
      // Only add max_tokens if specified
      if (maxTokens !== undefined) {
        requestOptions.max_tokens = maxTokens;
      }
      
      const response = await this.openai.chat.completions.create(requestOptions);

      if (!response.choices || response.choices.length === 0) {
        throw new Error('No choices received from GPT Vision analysis');
      }

      const content = response.choices[0]?.message?.content;
      console.log('✅ Vision complete');
      return content || '';
    } catch (error) {
      console.error('Error getting GPT Vision analysis:', error);
      throw error;
    }
  }

  /**
   * Generate image with DALL-E
   * @param prompt Image generation prompt
   * @returns Promise resolving to base64 image data
   */
  async generateImage(prompt: string): Promise<string> {
    try {
      console.log('🎨 Image generation');

      const response = await this.openai.images.generate({
        model: 'dall-e-3',
        prompt: prompt.trim(),
        n: 1,
        size: '1024x1024',
        response_format: 'b64_json',
      });

      // Get base64 data directly from response
      if (!response.data || response.data.length === 0) {
        throw new Error('No image data received from generation');
      }

      const base64Data = response.data[0]?.b64_json;
      if (!base64Data) {
        throw new Error('No base64 data received from image generation');
      }

      // Return as data URL for direct use in img src
      const dataUrl = `data:image/png;base64,${base64Data}`;
      console.log('✅ Image complete');
      return dataUrl;
    } catch (error) {
      console.error('Error generating image:', error);
      throw error;
    }
  }

  /**
   * Generate speech from text using OpenAI TTS
   * @param text Text to convert to speech
   * @returns Promise resolving to base64 audio data
   */
  async generateSpeech(text: string): Promise<string> {
    try {
      console.log('🔊 Audio generation');

      const response = await this.openai.audio.speech.create({
        model: 'tts-1',
        input: text,
        voice: 'nova',
        response_format: 'mp3'
      });

      // Convert response to blob
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });

      // Convert blob to base64 using FileReader
      const base64 = await this.blobToBase64(blob);
      console.log('✅ Audio complete');
      return base64;
    } catch (error) {
      console.error('Error generating speech:', error);
      throw error;
    }
  }

  /**
   * Convert a file to base64
   * @param file File to convert
   * @returns Promise resolving to base64 string
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Convert a blob to base64 data URL
   * @param blob Blob to convert
   * @returns Promise resolving to base64 data URL
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result); // This already includes the data URL prefix
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = (error) => reject(error);
    });
  }
}

const openAIService = new OpenAIService();
export default openAIService;
